/**
 * documentExport - REAL PNG/PDF export for the Global Document
 * Editor (2026-11-05).
 *
 * Until now the editor's PDF/PNG buttons called an onExport prop
 * nobody passed and alerted "Exported as PDF" while producing
 * nothing. This module renders the editor's actual document state
 * (template + the user's typed fields + photo + signature +
 * border) to a canvas at the document's REAL physical size and a
 * stated print resolution, then downloads it through the shared
 * save-as-download path.
 *
 * Physical sizes: passport data pages are 88 × 125 mm (ICAO TD3);
 * ID-1 cards (CA driver's licence, German Personalausweis) are
 * 85.6 × 54 mm; a US Social Security card is 3.375 × 2.25 in
 * (85.7 × 57.2 mm). Layout units map onto the physical page
 * (independent x/y scales), so the whole design lands on the
 * real-size page. Resolution is DOCUMENT_EXPORT_DPI, stated in
 * the UI next to the buttons.
 */
import { jsPDF } from 'jspdf';
import { saveBlobAsDownload } from './downloadFile';
import type { DocumentData, EditableField, PlacedDocItem } from './globalDocTemplates';
import type { BorderConfig } from '../components/BorderlineEngine';

export const DOCUMENT_EXPORT_DPI = 300;

const PHYSICAL_MM: Record<string, { w: number; h: number }> = {
    us_passport: { w: 88, h: 125 },
    uk_passport: { w: 88, h: 125 },
    ssn_card: { w: 85.7, h: 57.2 },
    california_dl: { w: 85.6, h: 54 },
    german_id: { w: 85.6, h: 54 }
};

/** The document's real-world page size in millimetres. */
export function documentPhysicalSizeMm(doc: DocumentData): { w: number; h: number } {
    const known = PHYSICAL_MM[doc.id];
    if (known) return { ...known };
    // Unknown (cloned) document: keep the layout's aspect ratio and
    // fit it inside the passport-page box, so the export is still a
    // real, stated size rather than an arbitrary pixel count.
    const aspect = doc.layout.width / Math.max(1, doc.layout.height);
    if (aspect >= 1) {
        const w = 125;
        return { w, h: Math.round((w / aspect) * 10) / 10 };
    }
    const h = 125;
    return { w: Math.round(h * aspect * 10) / 10, h };
}

/** Raster size in pixels for a document at the given DPI. */
export function documentExportPixels(doc: DocumentData, dpi: number = DOCUMENT_EXPORT_DPI): { width: number; height: number } {
    const mm = documentPhysicalSizeMm(doc);
    return {
        width: Math.round((mm.w / 25.4) * dpi),
        height: Math.round((mm.h / 25.4) * dpi)
    };
}

/** True when a #rrggbb background reads as light (dark text on top). */
export function isLightColor(hex: string): boolean {
    const m = /^#?([0-9a-f]{6})$/i.exec((hex || '').trim());
    if (!m) return false;
    const n = parseInt(m[1], 16);
    const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
    // Perceived luminance (ITU-R BT.601)
    return (0.299 * r + 0.587 * g + 0.114 * b) > 150;
}

/** The ink colour the preview uses for a field (explicit style wins; else auto-contrast). */
export function fieldTextColor(doc: DocumentData, field: EditableField): string {
    if (field.style?.color) return field.style.color;
    return isLightColor(doc.layout.backgroundColor) ? '#000000' : '#FFFFFF';
}

function loadImage(src: string): Promise<HTMLImageElement | null> {
    return new Promise(resolve => {
        try {
            const img = new Image();
            const timer = setTimeout(() => resolve(null), 3000);
            img.onload = () => { clearTimeout(timer); resolve(img); };
            img.onerror = () => { clearTimeout(timer); resolve(null); };
            img.src = src;
        } catch { resolve(null); }
    });
}

/** Load every photo/signature image the document carries, keyed by field id. */
export async function loadDocumentImages(doc: DocumentData, placedItems: PlacedDocItem[] = []): Promise<Map<string, HTMLImageElement>> {
    const images = new Map<string, HTMLImageElement>();
    await Promise.all([
        ...doc.fields
            .filter(f => (f.type === 'photo' || f.type === 'signature') && f.value)
            .map(async f => {
                const img = await loadImage(f.value);
                if (img) images.set(f.id, img);
            }),
        // Placed library items (parity pass): their images ride
        // the same map, keyed by item id.
        ...placedItems
            .filter(p => p.kind === 'image' && p.src)
            .map(async p => {
                const img = await loadImage(p.src!);
                if (img) images.set(p.id, img);
            })
    ]);
    return images;
}

type Ctx2D = CanvasRenderingContext2D;

function roundedRectPath(ctx: Ctx2D, x: number, y: number, w: number, h: number, r: number) {
    const rr = Math.max(0, Math.min(r, w / 2, h / 2));
    ctx.beginPath();
    ctx.moveTo(x + rr, y);
    ctx.lineTo(x + w - rr, y);
    ctx.arcTo(x + w, y, x + w, y + rr, rr);
    ctx.lineTo(x + w, y + h - rr);
    ctx.arcTo(x + w, y + h, x + w - rr, y + h, rr);
    ctx.lineTo(x + rr, y + h);
    ctx.arcTo(x, y + h, x, y + h - rr, rr);
    ctx.lineTo(x, y + rr);
    ctx.arcTo(x, y, x + rr, y, rr);
    ctx.closePath();
}

function drawBorder(ctx: Ctx2D, config: BorderConfig, width: number, height: number, sx: number, sy: number) {
    if (!config || config.style === 'NONE' || config.thickness <= 0) return;
    const s = Math.min(sx, sy);
    const lw = config.thickness * s;
    const radius = (config.cornerRadius || 0) * s;
    ctx.save();
    ctx.strokeStyle = config.color || '#000000';
    ctx.lineWidth = lw;
    if (config.style === 'DOTTED') ctx.setLineDash([lw * 0.2, lw * 1.6]);
    const inset = lw / 2 + 1;
    roundedRectPath(ctx, inset, inset, width - inset * 2, height - inset * 2, radius);
    ctx.stroke();
    if (config.style === 'DOUBLE' || config.style === 'DOUBLE_RULE' || config.style === 'DOUBLE_RULE_BOLD') {
        const gap = lw * 1.4;
        const inset2 = inset + gap + lw;
        roundedRectPath(ctx, inset2, inset2, width - inset2 * 2, height - inset2 * 2, Math.max(0, radius - gap));
        ctx.stroke();
    }
    ctx.restore();
}

function drawImageCover(ctx: Ctx2D, img: HTMLImageElement, x: number, y: number, w: number, h: number) {
    const iw = img.naturalWidth || img.width, ih = img.naturalHeight || img.height;
    if (!iw || !ih) return;
    const scale = Math.max(w / iw, h / ih);
    const sw = w / scale, sh = h / scale;
    ctx.save();
    ctx.beginPath(); ctx.rect(x, y, w, h); ctx.clip();
    ctx.drawImage(img, (iw - sw) / 2, (ih - sh) / 2, sw, sh, x, y, w, h);
    ctx.restore();
}

function drawImageContain(ctx: Ctx2D, img: HTMLImageElement, x: number, y: number, w: number, h: number) {
    const iw = img.naturalWidth || img.width, ih = img.naturalHeight || img.height;
    if (!iw || !ih) return;
    const scale = Math.min(w / iw, h / ih);
    const dw = iw * scale, dh = ih * scale;
    ctx.drawImage(img, x + (w - dw) / 2, y + (h - dh) / 2, dw, dh);
}

/**
 * Draw the document onto a 2D context sized width×height pixels.
 * Mirrors the on-screen preview's composition: background, border,
 * then fields in order — photo cover-cropped, signature contained,
 * text values with their field styles. Empty fields print NOTHING
 * (the preview's grey placeholder labels are a screen affordance,
 * not document content).
 */
export function drawDocument(
    ctx: Ctx2D,
    doc: DocumentData,
    borderConfig: BorderConfig,
    width: number,
    height: number,
    images: Map<string, HTMLImageElement>,
    placedItems: PlacedDocItem[] = []
): void {
    const sx = width / doc.layout.width;
    const sy = height / doc.layout.height;

    ctx.save();
    ctx.fillStyle = doc.layout.backgroundColor || '#FFFFFF';
    ctx.fillRect(0, 0, width, height);
    ctx.restore();

    drawBorder(ctx, borderConfig, width, height, sx, sy);

    for (const f of doc.fields) {
        const x = f.position.x * sx, y = f.position.y * sy;
        const w = f.position.width * sx, h = f.position.height * sy;
        if (f.type === 'photo') {
            const img = images.get(f.id);
            if (img) drawImageCover(ctx, img, x, y, w, h);
            continue;
        }
        if (f.type === 'signature') {
            const img = images.get(f.id);
            if (img) drawImageContain(ctx, img, x, y, w, h);
            continue;
        }
        if (!f.value) continue;
        const text = f.style?.textTransform === 'uppercase' ? f.value.toUpperCase() : f.value;
        const fontSize = (f.style?.fontSize || 11) * sy;
        ctx.save();
        ctx.beginPath(); ctx.rect(x, y, w, h); ctx.clip();
        ctx.font = `${f.style?.fontWeight || 'normal'} ${fontSize}px Arial, Helvetica, sans-serif`;
        ctx.fillStyle = fieldTextColor(doc, f);
        ctx.textBaseline = 'middle';
        ctx.fillText(text, x + 4 * sx, y + h / 2);
        ctx.restore();
    }

    // Placed library items (parity pass) draw ON TOP of the
    // fields, exactly as the preview layers them: images
    // contained in their box, text in the item's own size.
    for (const p of placedItems) {
        const x = p.position.x * sx, y = p.position.y * sy;
        const w = p.position.width * sx, h = p.position.height * sy;
        if (p.kind === 'image') {
            const img = images.get(p.id);
            if (img) drawImageContain(ctx, img, x, y, w, h);
            continue;
        }
        if (!p.text) continue;
        ctx.save();
        ctx.beginPath(); ctx.rect(x, y, w, h); ctx.clip();
        ctx.font = `${Math.max(8, 11 * sy)}px Arial, Helvetica, sans-serif`;
        ctx.fillStyle = isLightColor(doc.layout.backgroundColor) ? '#000000' : '#FFFFFF';
        ctx.textBaseline = 'middle';
        ctx.fillText(p.text, x + 2 * sx, y + h / 2);
        ctx.restore();
    }
}

export type DocumentCanvasFactory = (width: number, height: number) => HTMLCanvasElement;

const defaultCanvasFactory: DocumentCanvasFactory = (width, height) => {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    return canvas;
};

export async function renderDocumentCanvas(
    doc: DocumentData,
    borderConfig: BorderConfig,
    dpi: number = DOCUMENT_EXPORT_DPI,
    canvasFactory: DocumentCanvasFactory = defaultCanvasFactory,
    placedItems: PlacedDocItem[] = []
): Promise<HTMLCanvasElement> {
    const { width, height } = documentExportPixels(doc, dpi);
    const canvas = canvasFactory(width, height);
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas is not available in this browser');
    const images = await loadDocumentImages(doc, placedItems);
    drawDocument(ctx as Ctx2D, doc, borderConfig, width, height, images, placedItems);
    return canvas;
}

export function documentExportFilename(doc: DocumentData, ext: 'png' | 'pdf', dpi: number = DOCUMENT_EXPORT_DPI): string {
    const base = (doc.name || 'document').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'document';
    return `${base}-${dpi}dpi.${ext}`;
}

export interface DocumentExportResult {
    filename: string;
    widthPx: number;
    heightPx: number;
    sizeMm: { w: number; h: number };
    dpi: number;
}

function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
    return new Promise((resolve, reject) => {
        if (typeof canvas.toBlob === 'function') {
            canvas.toBlob(b => (b ? resolve(b) : reject(new Error('Could not encode the image'))), 'image/png');
        } else {
            try {
                const url = canvas.toDataURL('image/png');
                const bin = atob(url.split(',')[1] || '');
                const bytes = new Uint8Array(bin.length);
                for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
                resolve(new Blob([bytes], { type: 'image/png' }));
            } catch (e) { reject(e as Error); }
        }
    });
}

export async function exportDocumentPng(
    doc: DocumentData,
    borderConfig: BorderConfig,
    dpi: number = DOCUMENT_EXPORT_DPI,
    canvasFactory: DocumentCanvasFactory = defaultCanvasFactory,
    placedItems: PlacedDocItem[] = []
): Promise<DocumentExportResult> {
    const canvas = await renderDocumentCanvas(doc, borderConfig, dpi, canvasFactory, placedItems);
    const blob = await canvasToBlob(canvas);
    const filename = documentExportFilename(doc, 'png', dpi);
    await saveBlobAsDownload(blob, filename);
    const { width, height } = documentExportPixels(doc, dpi);
    return { filename, widthPx: width, heightPx: height, sizeMm: documentPhysicalSizeMm(doc), dpi };
}

export async function exportDocumentPdf(
    doc: DocumentData,
    borderConfig: BorderConfig,
    dpi: number = DOCUMENT_EXPORT_DPI,
    canvasFactory: DocumentCanvasFactory = defaultCanvasFactory,
    placedItems: PlacedDocItem[] = []
): Promise<DocumentExportResult> {
    const canvas = await renderDocumentCanvas(doc, borderConfig, dpi, canvasFactory, placedItems);
    const mm = documentPhysicalSizeMm(doc);
    const pdf = new jsPDF({
        orientation: mm.w >= mm.h ? 'landscape' : 'portrait',
        unit: 'mm',
        format: [mm.w, mm.h]
    });
    pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, mm.w, mm.h);
    const blob = pdf.output('blob') as Blob;
    const filename = documentExportFilename(doc, 'pdf', dpi);
    await saveBlobAsDownload(blob, filename);
    const { width, height } = documentExportPixels(doc, dpi);
    return { filename, widthPx: width, heightPx: height, sizeMm: mm, dpi };
}
